import './common.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { addServerErrors } from '@/components/forms/common/FormBase'
import { useModal } from '@/components/modal/Modal'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'

export interface InventoryGroupRow {
  id: string
  name: string
  parentId?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface InventoryGroupFormData {
  name: string
  parentId?: string | null
}

export const ROOT_INVENTORY_GROUP_ID = null

export const defaultInventoryGroupFormData = (parentId?: string | null): InventoryGroupFormData => ({
  name: '',
  parentId: parentId ?? null,
})

const getInventoryGroupChildren = (groups: InventoryGroupRow[], parentId: string | null) => {
  return groups
    .filter((group) => (group.parentId ?? null) === parentId)
    .sort((a, b) => a.name.localeCompare(b.name))
}

const getInventoryGroupPath = (groups: InventoryGroupRow[], selectedId: string | null) => {
  const path: InventoryGroupRow[] = []
  let currentId = selectedId

  while (currentId != null) {
    const row = groups.find((group) => group.id === currentId)
    if (!row) {
      break
    }
    path.unshift(row)
    currentId = row.parentId ?? null
  }

  return path
}

const collectDescendantIds = (groups: InventoryGroupRow[], rootId: string | undefined) => {
  if (rootId === undefined) {
    return new Set<string>()
  }

  const result = new Set<string>([rootId])
  const queue = [rootId]

  while (queue.length) {
    const currentId = queue.shift()
    if (currentId === undefined) {
      continue
    }

    const children = groups.filter((group) => (group.parentId ?? null) === currentId)
    for (const child of children) {
      if (!result.has(child.id)) {
        result.add(child.id)
        queue.push(child.id)
      }
    }
  }

  return result
}

export const InventoryGroupPathLabel = ({
  groups,
  selectedId,
  onClick,
}: {
  groups: InventoryGroupRow[]
  selectedId: string | null
  onClick: (row: InventoryGroupRow | null) => void
}) => {
  const { translates } = useAppContext()

  if (!translates) {
    return null
  }

  const path = getInventoryGroupPath(groups, selectedId)

  return <span className='remanent-locations-path'>
    <span className='node' onClick={() => onClick(null)}>
      {translates.for('elements.path.root')}
    </span>

    {path.map((row) => (
      <React.Fragment key={row.id}>
        {' > '}
        <span className='node' onClick={() => onClick(row)}>
          {row.name}
        </span>
      </React.Fragment>
    ))}
  </span>
}

export const normalizeInventoryGroupPayload = (data: InventoryGroupFormData): InventoryGroupFormData => {
  return {
    name: data.name.trim(),
    parentId: data.parentId ?? null,
  }
}

export const InventoryGroupRemovePopup = ({
  onClose,
  group,
  onRemoved,
}: {
  onClose: () => void
  group?: InventoryGroupRow
  onRemoved: () => void
}) => {
  const { services, translates } = useAppContext()
  const [error, setError] = useState<string | null>(null)

  if (!group || !translates) {
    return null
  }

  const remove = async () => {
    setError(null)
    const response = await services.session.fetchWithToken(`/api/communal/remanent/inventory-groups/byid/${group.id}/delete`, {
      method: 'DELETE',
    })
    if (response.status !== 200) {
      const message = await response.text()
      setError(message || translates.for('elements.table.inventory_groups.remove_error'))
      return
    }
    onRemoved()
    onClose()
  }

  return <div className='dialog'>
    <div className='dialog-text'>
      {translates.for('elements.table.inventory_groups.remove_ask').replace('{name}', group.name)}
    </div>
    <div className='dialog-btns'>
      <div className='dialog-btn' onClick={() => onClose()}>
        <i className='svgfont-no' />
        {translates.for('elements.dialog.yesno.no')}
      </div>
      <div className='dialog-btn' onClick={remove}>
        <i className='svgfont-yes' />
        {translates.for('elements.dialog.yesno.yes')}
      </div>
      {error ? <div className='dialog-error'>{error}</div> : null}
    </div>
  </div>
}

export const ParentInventoryGroupSelectPopup = ({
  onClose,
  groups,
  selectedId,
  blockedIds,
  onApprove,
}: {
  onClose: () => void
  groups: InventoryGroupRow[]
  selectedId: string | null
  blockedIds?: Set<string>
  onApprove: (id: string | null) => void
}) => {
  const { translates } = useAppContext()
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(selectedId ?? ROOT_INVENTORY_GROUP_ID)

  useEffect(() => {
    setCurrentNodeId(selectedId ?? ROOT_INVENTORY_GROUP_ID)
  }, [selectedId])

  const availableGroups = useMemo(() => {
    if (!blockedIds || blockedIds.size === 0) {
      return groups
    }
    return groups.filter((group) => !blockedIds.has(group.id))
  }, [groups, blockedIds])

  const currentChildren = useMemo(() => {
    return getInventoryGroupChildren(availableGroups, currentNodeId)
  }, [availableGroups, currentNodeId])

  if (!translates) {
    return null
  }

  return <div className='main-form'>
    <div className='form-field'>
      <label>{translates.for('popup.parent_inventory_group_select.current_group')}</label>
      <div className='dialog-text'>
        <InventoryGroupPathLabel groups={availableGroups} selectedId={currentNodeId} onClick={(row) => {
          if (!row) {
            setCurrentNodeId(ROOT_INVENTORY_GROUP_ID)
            return
          }
          setCurrentNodeId(row.id)
        }} />
      </div>
    </div>

    <div className='form-field'>
      <label>{translates.for('popup.parent_inventory_group_select.children')}</label>
      <div className='remanent-locations-search-body'>
        {currentChildren.length === 0 ? <div className='empty-search-table-message'>{translates.for('popup.parent_inventory_group_select.empty')}</div> : null}
        {currentChildren.map((row) => (
          <div
            key={row.id}
            className='row'
            onClick={() => setCurrentNodeId(row.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className='col'>{row.name}</div>
            <div className='col icocol'>
              <i className='svgfont-arrow-right' />
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className='form-buttons'>
      <button
        className='submit-button'
        type='button'
        onClick={() => {
          onApprove(currentNodeId)
          onClose()
        }}
      >
        {translates.for('popup.parent_inventory_group_select.accept')}
      </button>
    </div>
  </div>
}

export const InventoryGroupEditPopup = ({
  onClose,
  inventoryGroupId,
  defaultParentId,
  onSaved,
}: {
  onClose: () => void
  inventoryGroupId?: string
  defaultParentId?: string | null
  onSaved: () => void
}) => {
  const { services, session, translates } = useAppContext()
  const { openModal } = useModal()
  const form = useForm<InventoryGroupFormData>({
    mode: 'onChange',
    defaultValues: defaultInventoryGroupFormData(defaultParentId),
  })
  const formValues = form.watch()
  const [loading, setLoading] = useState<boolean>(!!inventoryGroupId)
  const [groups, setGroups] = useState<InventoryGroupRow[]>([])
  const [submitErrors, setSubmitErrors] = useState<string[]>([])

  useEffect(() => {
    services.session.fetchWithToken('/api/communal/remanent/inventory-groups').then(async (response) => {
      if (response.status !== 200) {
        setGroups([])
        return
      }
      const data = await response.json()
      setGroups(Array.isArray(data) ? data : [])
    })
  }, [])

  useEffect(() => {
    if (inventoryGroupId === undefined) {
      form.reset(defaultInventoryGroupFormData(defaultParentId))
      setLoading(false)
      return
    }

    setLoading(true)
    services.session.fetchWithToken(`/api/communal/remanent/inventory-groups/byid/${inventoryGroupId}`)
      .then(async (response) => {
        if (response.status !== 200) {
          throw new Error(await response.text())
        }
        return response.json()
      })
      .then((data: InventoryGroupRow) => {
        form.reset({
          name: data.name || '',
          parentId: data.parentId ?? null,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [inventoryGroupId, defaultParentId])

  useEffect(() => {
    if (submitErrors.length === 0 && Object.keys(form.formState.errors).length === 0) {
      return
    }
    setSubmitErrors([])
    form.clearErrors()
  }, [formValues.name, formValues.parentId])

  const handle = async (data: InventoryGroupFormData) => {
    setSubmitErrors([])
    const payload = normalizeInventoryGroupPayload(data)
    const url = inventoryGroupId === undefined
      ? '/api/communal/remanent/inventory-groups'
      : `/api/communal/remanent/inventory-groups/byid/${inventoryGroupId}/update`
    const method = inventoryGroupId === undefined ? 'POST' : 'PUT'

    const response = await services.session.fetchWithToken(url, {
      method,
      body: JSON.stringify(payload),
    })

    if (response.status !== 200) {
      const errors = await response.json().catch(() => null)
      if (errors) {
        addServerErrors(form, session.translates, errors)
        const translatedErrors = Object.values(errors as Record<string, { message?: string }>).map((error) => {
          if (!error?.message) {
            return translates.for('errors.general_error')
          }
          return translates.for(error.message)
        })
        setSubmitErrors(translatedErrors)
      } else {
        setSubmitErrors([translates.for('errors.general_error')])
      }
      return
    }

    onSaved()
    onClose()
  }

  if (!session || !translates || loading) {
    return <Spinner />
  }

  const currentId = inventoryGroupId
  const blockedIds = collectDescendantIds(groups, currentId)
  const selectedParentId = form.watch('parentId') ?? null

  return <form className='main-form' onSubmit={form.handleSubmit(handle)}>
    <div className='form-field'>
      <label>{translates.for('elements.table.inventory_groups.name')}</label>
      <input type='text' {...form.register('name', { required: true })} />
    </div>

    {inventoryGroupId ? <div className='form-field'>
      <label>{translates.for('elements.table.inventory_groups.parent')}</label>
      <InventoryGroupPathLabel groups={groups} selectedId={selectedParentId} onClick={(row) => {
        if (!row) {
          form.setValue('parentId', ROOT_INVENTORY_GROUP_ID, {
            shouldDirty: true,
            shouldValidate: true,
          })
          return
        }
        if (selectedParentId === row.id) {
          return openModal((props) => <ParentInventoryGroupSelectPopup
            {...props}
            groups={groups}
            selectedId={row.id}
            blockedIds={blockedIds}
            onApprove={(value) => {
              form.setValue('parentId', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }}
          />)
        }
        form.setValue('parentId', row.id, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }} />
    </div> : null}

    <div className='form-buttons'>
      <button className='submit-button' type='submit'>
        {inventoryGroupId === undefined
          ? translates.for('elements.table.inventory_groups.add')
          : translates.for('elements.table.inventory_groups.save')}
      </button>
    </div>
    {submitErrors.length > 0 ? <div className='critic-error-box'>
      {submitErrors.map((error, index) => (
        <div key={error + index}>{error}</div>
      ))}
    </div> : null}
  </form>
}
